import { ecId, ecLean, handBack, record, type JanusCtx, type Row } from '../ability/context';
import { statusItemId, statusRows, statusRemovalBefore, STATUS_IDS } from '../statuses/expiry';
import { statusScalar } from '../actions/activity';
import { encounterCreatureField } from '../../shared/array-types';
import { runTriggerFamily } from '../ability/trigger-executor';
import { consentPending, TRIGGER_CONSENT_REASON } from '../ability/trigger-consent';
import { readInventoryField } from '../compile/inventory-raw-readers';

export function summonControllerId(target: Row): string {
    return String(encounterCreatureField(record(target.encounter_creatures), 'controller_encounter_creature_id') ?? '').toUpperCase();
}

/** A removal can reach a live tagged summon through another connection with the same source. */
export function holdsTaggedSummon(members: Iterable<Row>): boolean {
    return [...members].some(member => summonControllerId(member) && Number(ecLean(member, 'encounter_creature_deleted') ?? 0) === 0
        && Number(ecLean(member, 'encounter_creature_dead') ?? 0) === 0
        && statusRows(member, 'target').some(status => statusItemId(status) === STATUS_IDS.summon_tag));
}

/** Native is_creature_a_summon: a PC controller field alone is not a summon kind. */
export function isOrdinarySummon(target: Row): boolean {
    return String(ecLean(target, 'item_type') ?? '') === 'creature' && Boolean(summonControllerId(target))
        && !encounterCreatureField(record(target.encounter_creatures), 'summon_type');
}

export function isHunterSummon(target: Row): boolean {
    return String(ecLean(target, 'item_type') ?? '') === 'creature' && Boolean(summonControllerId(target))
        && String(encounterCreatureField(record(target.encounter_creatures), 'summon_type') ?? '').toUpperCase() === '4E601894-014E-32FB-E138-2A261969B7B2';
}

export function isAnimatedSummon(target: Row): boolean {
    return String(ecLean(target, 'item_type') ?? '') === 'creature' && Boolean(summonControllerId(target))
        && String(encounterCreatureField(record(target.encounter_creatures), 'summon_type') ?? '').toUpperCase() === '5D19BA1F-975B-4CB8-457C-0ECE28B72BF8';
}

/** Both damage deaths and source-triggered departures notify the held controller once. */
export function triggerSummonDeath(ctx: JanusCtx, target: Row): void {
    const controller = ctx.members.get(summonControllerId(target));
    if (!controller) handBack('death:summon-controller-not-held');
    runTriggerFamily(ctx, { trigger: 'triggers_on_caster_summon_death' }, controller, target, [target], false);
    if (!ctx.dryRun && consentPending(ctx.read)) handBack(`outcome:${TRIGGER_CONSENT_REASON}`);
}

/** Native summon-tag removal selection, after the status rows have been retired. Removing
 * one tag leaves live siblings alone; removing its source also matches live caster/origin tags.
 * Dead/deleted creatures never receive a second death tail. Historical tags remain inputs. */
export function summonsOnStatusRemoved(removed: Row, historical: readonly Row[], live: readonly Row[], members: readonly Row[], holders: readonly Row[] = members): Row[] {
    const id = (row: Row, key: string): string => String(statusScalar(row, key) ?? '').toUpperCase();
    // The normalized row reader restores an omitted self-caster and preserves an explicit
    // empty environmental caster, exactly as the persisted source used by the native query.
    const caster = (row: Row): unknown => statusRemovalBefore(row, id(row, 'status_target_id')).status_caster_id;
    const removedId = id(removed, 'status_id'), connection = id(removed, 'status_connected_id');
    const tagRemoved = statusItemId(removed) === STATUS_IDS.summon_tag;
    const liveIds = new Set(live.map(row => id(row, 'status_id')));
    // The native LEFT JOIN excludes inventory/status anchors. A known non-creature target is
    // different from a creature whose row has not been hydrated yet.
    const inventories = new Set(holders.map(row => String(readInventoryField(row, 'inventory_id') ?? '').toUpperCase()).filter(Boolean));
    const matched = new Set(historical.filter(tag => !inventories.has(id(tag, 'status_target_id')) && statusItemId(tag) === STATUS_IDS.summon_tag && (
        id(tag, 'status_id') === removedId
        || id(tag, 'status_connected_id') === connection && (!tagRemoved || !liveIds.has(id(tag, 'status_id')))
        || !tagRemoved && liveIds.has(id(tag, 'status_id')) && caster(tag) === caster(removed)
            && id(tag, 'status_origin_id') === id(removed, 'status_origin_id')
    )).map(tag => id(tag, 'status_target_id')));
    if ([...matched].some(targetId => !members.some(member => ecId(member) === targetId)))
        handBack('death:summon-target-not-held');
    return members.filter(member => matched.has(ecId(member)) && Boolean(summonControllerId(member))
        && Number(ecLean(member, 'encounter_creature_dead') ?? 0) === 0 && Number(ecLean(member, 'encounter_creature_deleted') ?? 0) === 0);
}
